// Builds a token's carry curve from its vol surface (per-tenor "Future" =
// forward price, "DF" = USD discount factor, "T" = year fraction — all
// returned by implied_volatility_smile's surface.expiry.tenors, one call
// per token — see use-implied-vol-smile.ts) and that same call's spot. See
// types.ts for the full curve taxonomy + the Basis tautology caveat.
import { VolSurfaceTenors } from '../../providers/exotics-tokens/use-implied-vol-smile';
import { CarryCurvePoint } from './types';

// Fallback only — the surface's own "T" field is preferred (server-side
// ACT/365 convention, matches build_calibration_snapshot.py). A bare
// "YYYY-MM-DD" expiry is padded to 08:00:00 UTC per the FastAPI/engine
// convention (see engine-wire.ts's buildEngineInstrument).
const expiryToDate = (tenorKey: string): Date => {
  const withTime = tenorKey.length > 10 ? tenorKey : `${tenorKey} 08:00:00`;
  return new Date(withTime.replace(' ', 'T') + 'Z');
};

const ACT365_MS = 365 * 24 * 60 * 60 * 1000;

export function buildCarryCurve(tenors: VolSurfaceTenors, spot: number | null): CarryCurvePoint[] {
  if (spot === null || !Number.isFinite(spot) || spot <= 0) return [];

  const points: CarryCurvePoint[] = [];
  const now = Date.now();

  for (const [tenorKey, labels] of Object.entries(tenors)) {
    const forward = labels.Future;
    if (typeof forward !== 'number' || !Number.isFinite(forward) || forward <= 0) continue;

    const expiryMs = expiryToDate(tenorKey).getTime();
    const yearFrac =
      typeof labels.T === 'number' && Number.isFinite(labels.T) && labels.T > 0
        ? labels.T
        : (expiryMs - now) / ACT365_MS;
    if (!Number.isFinite(yearFrac) || yearFrac <= 0) continue;

    const forwardCarry = Math.log(forward / spot) / yearFrac;
    const forwardPoints = forward - spot;

    // USD Discount + Base Discount (covered interest parity: DF_base =
    // DF_usd * Future/Spot) only when the surface actually returned a
    // usable DF — dfUsd/forward/spot all come from this same
    // implied_volatility_smile call, so the ratio is self-consistent.
    const dfUsd = labels.DF;
    let usdDf: number | null = null;
    let usdDiscount: number | null = null;
    let baseDf: number | null = null;
    let baseDiscount: number | null = null;
    let forwardFactor: number | null = null;
    if (typeof dfUsd === 'number' && Number.isFinite(dfUsd) && dfUsd > 0) {
      usdDf = dfUsd;
      usdDiscount = -Math.log(dfUsd) / yearFrac;
      baseDf = dfUsd * (forward / spot);
      baseDiscount = -Math.log(baseDf) / yearFrac;
      forwardFactor = baseDf / usdDf;
    }

    // Basis = Forward Carry (options) - Futures Carry — only meaningful
    // where both exist, so left null here and filled in by
    // attach-futures-snapshot.ts once futuresCarry (which needs a real
    // listed future at this exact expiry) is known.
    const basis: number | null = null;

    points.push({
      tenor: tenorKey.slice(0, 10),
      expiryMs,
      yearFrac,
      future: forward,
      forwardPoints,
      forwardFactor,
      usdDf,
      usdDiscount,
      baseDf,
      baseDiscount,
      forwardCarry,
      basis,
      futuresMark: null,
      futuresFwdPoints: null,
      futuresForwardFactor: null,
      futuresCarry: null,
    });
  }

  return points.sort((a, b) => a.tenor.localeCompare(b.tenor));
}
